import {
  resolveCategoryFill,
  toPaletteColorName,
} from '@/features/colors/lib/paletteLookup';
import { getDisplayEmoji } from '@/features/events/lib/eventEmoji.lib';
import type { DecoratedExperienceEvent } from '@/features/events/types';
import { usePageExperienceContext } from '@/features/experiences/contexts/PageExperienceContext';
import { useTimeFormat } from '@/features/experiences/hooks/useTimeFormat';
import { softTint, surfaceTint } from '@/features/experiences/lib/tint';
import { PillsList } from '@/pages/Schedule/components/Pills';
import type { ExperienceFeed } from '@/types/gql.generated';
import { Box, Flex, Icon, Text } from '@/ui';
import { formatTime } from '@/utils/formatTime';
import { UNCATEGORIZED_FEED_COLOR } from '../feedColors';
import { SectionLabel } from './SectionLabel';

type Props = {
  feed: ExperienceFeed;
  previewEvent: DecoratedExperienceEvent | null;
};

export const PreviewCard = ({ feed, previewEvent }: Props) => {
  const { experience } = usePageExperienceContext();
  const timeFormat = useTimeFormat();
  // The assigned category drives both the feed's name and its color. With no
  // category, the preview is a neutral gray card (matching the source rows) and
  // the name strip is hidden.
  const category = feed.category;
  // The preview fully adopts the category's color scheme (background AND
  // text) for the experience's bold/light colorMode — it is a preview of how
  // the category color renders. Unassigned feeds keep the gray scheme.
  const fill = category
    ? resolveCategoryFill(
        toPaletteColorName(category.colorName),
        experience.colorPalette,
        experience.colorMode
      )
    : null;
  const nameStripBg = fill?.background ?? surfaceTint(UNCATEGORIZED_FEED_COLOR);
  const cardBg = fill?.background ?? softTint(UNCATEGORIZED_FEED_COLOR);
  const textColor = fill?.text ?? 'ink.strong';
  const subTextColor = fill?.text ?? 'ink.soft';

  // Default labels and who-labels the feed applies to its events.
  const previewLabels = [...feed.labels, ...feed.whoLabels];

  // Resolved the way the page resolves it, so the preview shows the default
  // taking over from whatever emoji the event arrived with — which is the rule
  // the control needs to communicate.
  const previewEmoji = previewEvent
    ? getDisplayEmoji(experience, previewEvent)
    : null;

  return (
    <Box
      borderLeftColor="line.DEFAULT"
      borderLeftStyle="solid"
      borderLeftWidth="1px"
      p={6}
    >
      <SectionLabel>Preview</SectionLabel>
      <Box
        borderRadius="ah-xl"
        boxShadow="0 4px 14px rgba(0,0,0,.06)"
        overflow="hidden"
      >
        {category?.text && (
          <Box bg={nameStripBg} px={3} py={2}>
            <Text
              color={textColor}
              fontSize="11.5px"
              fontWeight={700}
              noOfLines={1}
            >
              {category.text}
            </Text>
          </Box>
        )}
        <Box bg={cardBg} px={3} py={3}>
          {previewEvent ? (
            <>
              <Flex align="baseline" gap={2}>
                {previewEmoji && (
                  <Text flexShrink={0} fontSize="sm">
                    {previewEmoji}
                  </Text>
                )}
                <Text
                  color={textColor}
                  fontSize="ah-control"
                  fontWeight={700}
                  noOfLines={1}
                >
                  {previewEvent.title}
                </Text>
              </Flex>
              <Text color={subTextColor} fontSize="ah-status" mt={1}>
                {previewEvent.isAllDay
                  ? 'All day'
                  : formatTime(previewEvent.startDate, timeFormat)}
              </Text>
            </>
          ) : (
            <Text color="ink.muted" fontSize="ah-status">
              No events yet
            </Text>
          )}
          {previewLabels.length > 0 && (
            <PillsList labels={previewLabels} mt={2} />
          )}
          <Flex justify="flex-end" mt={3}>
            {/* Lightning bolt marks a feed event (see EventOrigin). */}
            <Icon color={subTextColor} icon="Zap" size={3} />
          </Flex>
        </Box>
      </Box>
    </Box>
  );
};
